/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

"use client";

import { isEUCountry } from "@cosmos/common-utils";
import {
	CosmosComponentContextProps,
	CosmosRichText,
	CosmosTitle,
	LegalBanner,
	ResponsiveContainer,
	richTextContentStyles,
	SectionContainer,
	useScrollClientHash,
} from "@cosmos/components";
import { Eula } from "@cosmos/eula-public-service";
import { PageEulaKeyFragment } from "@cosmos/hygraph-schema";
import { useAccountCountry, useGeo } from "@cosmos/next-hooks";
import { Select, Option } from "@eds-web/react";
import { useMessages } from "@epic-mw/i18n";
import { notFound, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState, useMemo } from "react";

import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import { TemplateProviders } from "../templates";

// Helper function to get user's country code
const getUserCountryCode = (accountCountry: string, geoCountry?: string): string => {
	return accountCountry || geoCountry || "";
};

// Helper function to find matching group agreement based on country
const findMatchingGroupAgreement = (groupAgreements: Eula[], countryCode: string): Eula | null => {
	if (!groupAgreements?.length || !countryCode) {
		return null;
	}

	// Check for DE-specific agreement
	if (countryCode.toUpperCase() === "DE") {
		const deAgreement = groupAgreements.find(
			(agreement) => agreement.key?.toLowerCase().endsWith("_de") && agreement.title && agreement.body,
		);
		if (deAgreement) return deAgreement;
	}

	// Check for EU agreement
	if (isEUCountry(countryCode)) {
		const euAgreement = groupAgreements.find(
			(agreement) => agreement.key?.toLowerCase().endsWith("_eu") && agreement.title && agreement.body,
		);
		if (euAgreement) return euAgreement;
	}

	return null;
};

interface Props {
	pageData?: PageEulaKeyFragment;
	componentContext: CosmosComponentContextProps;
	eula?: Eula;
	groupAgreements?: Eula[];
	isGameMode?: boolean;
	locale?: string;
}

export const CosmosPageEulaKey = ({
	pageData,
	componentContext,
	eula,
	groupAgreements,
	isGameMode,
	locale,
}: Props): React.JSX.Element => {
	if (!eula?.key || !eula?.title || !eula?.body) {
		notFound();
	}

	const router = useRouter();
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const messages = useMessages();
	const accountCountry = useAccountCountry();
	const { geo } = useGeo();

	const disclaimer = pageData?.disclaimer;
	const pageTitle = pageData?.title;
	const subtitle = pageData?.subtitle;

	const validGroupAgreements = groupAgreements && groupAgreements?.length > 1;
	const paramEulaKey = searchParams.get("eulaKey");
	const paramAgreement = validGroupAgreements ? groupAgreements?.find((a) => a.key === paramEulaKey) : null;

	// Get user's country code
	const userCountryCode = validGroupAgreements ? getUserCountryCode(accountCountry, geo?.country) : null;
	// Find matching group agreement based on country
	const matchingAgreement =
		validGroupAgreements && userCountryCode ? findMatchingGroupAgreement(groupAgreements, userCountryCode) : null;

	let activeEula = eula;

	const paramAgreementLocale = paramAgreement?.locale === "en" ? "en-US" : paramAgreement?.locale;
	if (paramAgreement?.title && paramAgreement?.body && paramAgreementLocale === locale) {
		if (activeEula.key !== paramAgreement.key) {
			activeEula = paramAgreement;
		}
	} else if (matchingAgreement && activeEula.key !== matchingAgreement.key) {
		activeEula = matchingAgreement;
	}

	const [selected, setSelected] = useState<Option<string>>({
		label: activeEula?.title || "",
		value: activeEula?.key || "",
	});

	const title = pageTitle || activeEula.title;

	// Memoize the options array for the Select component
	const selectOptions = useMemo(() => {
		if (!groupAgreements) return [];
		return groupAgreements.map((a) => {
			const aLocale = a?.locale === "en" ? "en-US" : a?.locale;
			if (a.key === activeEula.key && aLocale !== locale) {
				return { label: eula.title, value: eula.key };
			}
			return { label: a.title, value: a.key };
		});
	}, [groupAgreements, activeEula.key, activeEula.title, locale]);

	const onAgreementChange = useCallback(
		(option: Option<string>) => {
			setSelected(option);
			const newActive = groupAgreements?.find((a) => a.key === option.value);
			if (newActive) {
				const newParams = new URLSearchParams(searchParams);
				newParams.set("eulaKey", newActive.key);
				router.replace(`${pathname}?${newParams.toString()}`);
			}
		},
		[groupAgreements, searchParams, router, pathname, eula, locale],
	);

	useScrollClientHash();

	return (
		<div id="eula-key-template">
			<TemplateProviders componentContext={componentContext}>
				<LegalBanner banner={pageData?.banner} bannerCountryCodes={pageData?.bannerCountryCodes} />
				<ResponsiveContainer>
					<SectionContainer>
						{validGroupAgreements ? (
							<div
								className={cx(
									"regional-select",
									css({
										width: "340px",
										maxWidth: "full",
										marginLeft: "auto",
										paddingBottom: "eds.dimension.24",
										mdDown: {
											marginInlineEnd: "auto",
											textAlign: "center",
											"& > div": {
												//override EDS
												alignItems: "center !important",
											},
										},
									}),
								)}
							>
								<Select
									label={messages["epic.cosmos.agreements"] || "Agreements"}
									clearable={false}
									value={selected}
									options={selectOptions}
									onChange={onAgreementChange}
								></Select>
							</div>
						) : null}
						<VStack gap="eds.dimension.48" alignItems={"flex-start"}>
							<VStack gap="eds.dimension.16" className={css({ margin: "0 auto" })}>
								{disclaimer && <CosmosRichText html={disclaimer} />}
								{title && (
									<CosmosTitle
										title={{ text: title, variant: "heading2XL", as: "h1" }}
										className={cx(
											"eula-key-title",
											css({
												textAlign: "center",
												mdDown: {
													fontSize: "28px",
												},
											}),
										)}
									/>
								)}
								{subtitle && <CosmosRichText html={subtitle} />}
							</VStack>
							<div className={cx("eula-key-body", css({ maxWidth: "full" }), richTextContentStyles)}>
								<CosmosRichText html={eula.body} isGameMode={isGameMode} />
							</div>
						</VStack>
					</SectionContainer>
				</ResponsiveContainer>
			</TemplateProviders>
		</div>
	);
};
